import React, { useState } from 'react';

import { useFormik } from 'formik';
import _ from 'lodash';
import { useDispatch } from 'react-redux';

import { changePassword } from 'api/endpoints/user';
import Button from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import Input from 'components/Input';
import { ENUM_PASSWORD_CREATE_ERROR } from 'constants/enum';
import { MESSAGE_COMMON, MESSAGE_PROFILE } from 'constants/string/messages';
import { TEXT_COMMON, TEXT_PROFILE } from 'constants/string/texts';
import { toastError, toastSuccess } from 'lib/toast';
import { setIsAuthenticated } from 'store/actions/auth';
import { setLoading } from 'store/actions/common';
import { clearModalProfile } from 'store/actions/profile';
import { checkPasswordRegister } from 'utils/helper';
import { clearLocalStorage } from 'utils/localStorage';

const UpdatePassword = ({ back }) => {
  const dispatch = useDispatch();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const initialValues = {
    old_password: '',
    password: '',
    passwordConfirm: '',
  };

  const handleUpdatePassword = async (values) => {
    try {
      dispatch(setLoading({ isOpen: true }));
      await changePassword(values)
        .then(async (response) => {
          clearLocalStorage();
          dispatch(setIsAuthenticated(false));
          dispatch(clearModalProfile());
          toastSuccess(response.message || '');
        })
        .catch((errors) => {
          toastError(errors.message);
        });
      dispatch(setLoading({ isOpen: false }));
    } catch {
      // Handle update error
      dispatch(setLoading({ isOpen: false }));
    }
  };

  const form = useFormik({
    initialValues: initialValues,
    validateOnChange: isSubmitted,
    validateOnBlur: false,
    validate: (values) => {
      const errors = {};
      if (!values.old_password) errors.old_password = true;
      if (!values.password || checkPasswordRegister(values.password).length > 0) errors.password = checkPasswordRegister(values.password);
      if (!values.passwordConfirm || values.password !== values.passwordConfirm) errors.passwordConfirm = true;
      setIsSubmitted(true);
      return errors;
    },
    onSubmit: async (values) => {
      await handleUpdatePassword(values);
    },
  });

  return (
    <>
      <div className="flex-between-center px-6 py-4 border-b border-white-720">
        <p className="font-bold">{TEXT_PROFILE.TITLE_PASSWORD_EDITING}</p>
      </div>

      <div className="p-6">
        <fieldset>
          <p className="text-black/85 mb-2 flex items-center">
            <span className="text-danger-411 mr-1">
              <img src={require('assets/resources/Required.svg').default} alt="required" width={7} height={7} />
            </span>
            {TEXT_PROFILE.OLD_PASSWORD}
          </p>
          <Input
            id="old_password"
            name="old_password"
            type="password"
            value={form.values.old_password}
            onChange={form.handleChange}
            className="w-full"
            invalid={form.errors.old_password}
          />
          <p className={'text-sm text-danger-229'}>{form.errors?.old_password && <ErrorMessage>{MESSAGE_COMMON.NOT_EMPTY}</ErrorMessage>}</p>
        </fieldset>

        <fieldset>
          <p className="text-black/85 mt-4 mb-2 flex items-center">
            <span className="text-danger-411 mr-1">
              <img src={require('assets/resources/Required.svg').default} alt="required" width={7} height={7} />
            </span>
            {TEXT_PROFILE.NEW_PASSWORD}
          </p>
          <Input
            id="new_password"
            type="password"
            value={form.values.password}
            onChange={form.handleChange}
            name="password"
            className="w-full"
            invalid={form.errors?.password}
          />
          <div className={`text-sm ${form.errors?.password ? 'text-danger-229' : 'text-secondary-354'}`}>
            {!form.values.password && form.errors.password ? (
              <ErrorMessage>{MESSAGE_COMMON.NOT_EMPTY}</ErrorMessage>
            ) : (
              <>
                {form.errors.password && (
                  <ErrorMessage>
                    <div>
                      <p>{MESSAGE_PROFILE.PASSWORD_INVALID_TEXT}</p>
                      <p>{MESSAGE_PROFILE.PASSWORD_INVALID_REQUIRED_TEXT}</p>
                    </div>
                  </ErrorMessage>
                )}
                {!form.errors.password && <p>{TEXT_PROFILE.PASSWORD_REQUIRED_TEXT}</p>}
                <ul className={`list-disc ${form.errors?.password ? 'ml-11' : 'ml-6'}`}>
                  {(!form.errors.password || (form.errors.password && form.errors.password.includes(ENUM_PASSWORD_CREATE_ERROR.LENGTH))) && (
                    <li>{TEXT_COMMON.PASSWORD_REQUIRED_LENGTH}</li>
                  )}
                  {(!form.errors.password ||
                    (form.errors.password &&
                      _.intersection(form.errors.password || [], [
                        ENUM_PASSWORD_CREATE_ERROR.HAS_LOWERCASE,
                        ENUM_PASSWORD_CREATE_ERROR.HAS_UPPERCASE,
                        ENUM_PASSWORD_CREATE_ERROR.HAS_NUMBER,
                      ]).length > 0)) && <li>{TEXT_COMMON.PASSWORD_REQUIRED}</li>}
                  {(!form.errors.password || (form.errors.password && form.errors.password.includes(ENUM_PASSWORD_CREATE_ERROR.HAS_FULL_WIDTH))) && (
                    <li>{TEXT_COMMON.PASSWORD_REQUIRED_CHARACTER}</li>
                  )}
                  {(!form.errors.password ||
                    (form.errors.password &&
                      _.intersection(form.errors.password || [], [
                        ENUM_PASSWORD_CREATE_ERROR.HAS_SYMBOLS,
                        ENUM_PASSWORD_CREATE_ERROR.HAS_SYMBOLS_REQUIRED,
                      ]).length > 0)) && <li>{TEXT_COMMON.PASSWORD_REQUIRED_SYMBOL}</li>}
                </ul>
              </>
            )}
          </div>
        </fieldset>

        <fieldset>
          <p className="text-black/85 mt-4 mb-2 flex items-center">
            <span className="text-danger-411 mr-1">
              <img src={require('assets/resources/Required.svg').default} alt="required" width={7} height={7} />
            </span>
            {TEXT_PROFILE.NEW_PASSWORD_CONFIRM}
          </p>
          <Input
            id="password-confirm"
            type="password"
            name="passwordConfirm"
            value={form.values.passwordConfirm}
            onChange={form.handleChange}
            className="w-full"
            invalid={form.errors?.passwordConfirm}
          />
          <p className={`text-sm ${form.errors.passwordConfirm ? 'text-danger-229' : 'text-secondary-354'}`}>
            {!form.values.passwordConfirm && form.errors.passwordConfirm && <ErrorMessage>{MESSAGE_COMMON.NOT_EMPTY}</ErrorMessage>}
            {form.values.passwordConfirm && form.errors.passwordConfirm && <ErrorMessage>{MESSAGE_PROFILE.PASSWORD_MATCHING}</ErrorMessage>}
          </p>
        </fieldset>
      </div>

      <div className="h-16 border-t border-white-720 flex justify-end items-center gap-2 py-4 px-6">
        <Button className="h-8 text-sm" text={TEXT_COMMON.CANCEL_TEXT} color="outline" onClick={back} />
        <Button className="h-8 text-sm" text={TEXT_COMMON.RENEW} color="primary" onClick={form.handleSubmit} />
      </div>
    </>
  );
};

export default React.memo(UpdatePassword);
